// Global Instructions Rule Applied!
// Detail shape of GET /fund-transactions/:id and POST /fund-transactions
// (ft__transaction_detail): header + items + journal preview + totals, and on
// save additionally the ft__generate_journals result under `generation`.
// Keys are raw column names (to_jsonb of the row) plus joined labels, so the
// schemas are passthrough on the row objects while pinning the fields the UI
// consumes.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const FundTransactionHeaderSchema = z
  .object({
    id: z.string(),
    fund_id: z.string(),
    transaction_type_id: z.number(),
    transaction_date: z.string(),
    description: z.string().nullish(),
    status_id: z.number().nullish(),
    currency_id: z.string().nullish(),
    capital_call_id: z.string().nullish(),
    // a distribution booked through wf__issue_distribution (phase 3)
    distribution_id: z.string().nullish(),
    capital_contribution_id: z.string().nullish(),
    fee_period_start: z.string().nullish(),
    fee_period_end: z.string().nullish(),
    trashed: z.boolean().nullish(),
    transaction_type_label: z.string().nullish(),
    status_label: z.string().nullish(),
    fund_name: z.string().nullish()
  })
  .passthrough()

export const FundTransactionItemSchema = z
  .object({
    id: z.string(),
    fund_transaction_id: z.string(),
    acc_item_id: z.string(),
    acc_item_label: z.string().nullish(),
    description: z.string().nullish(),
    amount: z.number(),
    currency_id: z.string().nullish(),
    display_order: z.number().nullish(),
    // set on the engine-managed mirror item of a fee-capital funding source
    // (spec A3.8) - never a user line
    fee_funding_source_id: z.string().nullish()
  })
  .passthrough()

export const FundTransactionJournalSchema = z
  .object({
    id: z.string(),
    journal_date: z.string().nullish(),
    item_id: z.string().nullish(),
    item_label: z.string().nullish(),
    debit_credit_id: z.string(),
    amount: z.number(),
    description: z.string().nullish(),
    fund_transaction_id: z.string().nullish(),
    fund_transaction_item_id: z.string().nullish(),
    // the LP subledger dimension; NULL = a fund-level line
    fund_commitment_id: z.string().nullish(),
    // the commitment's holder as at the line's date (the fund ledger's rule)
    investors__name: z.string().nullish(),
    is_manual: z.boolean()
  })
  .passthrough()

export const FundTransactionTotalsSchema = z
  .object({
    debits: z.number(),
    credits: z.number(),
    difference: z.number()
  })
  .passthrough()

export const FundTransactionGenerationSchema = z
  .object({
    status: z.string(),
    message: z.string().nullish()
  })
  .passthrough()

// per-LP fee allocation of a Management Fees transaction (A3.5): calculated
// from the fee engine, override manual; journals book COALESCE(override,
// calculated)
export const FundTransactionFeeAllocationSchema = z
  .object({
    id: z.string(),
    fund_commitment_id: z.string(),
    investors__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    calculated_amount: z.number().nullish(),
    override_amount: z.number().nullish(),
    effective_amount: z.number().nullish(),
    prog__due_to_date: z.number().nullish(),
    // § 11.9(b) cross-credit on a primary fund (0 elsewhere)
    prog__cross_credit_to_date: z.number().nullish(),
    // fees booked BEFORE this transaction (never its own journals): the
    // row reads due - cross-credit - booked before = calculated
    prog__booked_before: z.number().nullish(),
    // the position now (this transaction included once Booked)
    prog__booked_to_date: z.number().nullish(),
    prog__due_current: z.number().nullish(),
    prog__delta_total: z.number().nullish(),
    // the part of the effective fee that is a fee on assumed leverage, paid
    // from leverage capital and outside fee capital; null unless the fund is
    // on the 'Regulatory capital + assumed leverage' basis
    prog__leverage_fee: z.number().nullish(),
    // fee capital tracking (spec A3.8), per the commitment's tracking unit
    // (its subscription in a consolidated fund); null when the fund is not
    // tracked: fee capital left before this transaction, the part of this
    // payment it covers, what this transaction funds from partnership
    // assets (negative = reversal) and what is left after
    prog__fee_capital_before: z.number().nullish(),
    prog__covered_by_fee_capital: z.number().nullish(),
    prog__from_assets: z.number().nullish(),
    prog__fee_capital_after: z.number().nullish()
  })
  .passthrough()

export type FundTransactionFeeAllocation = z.infer<typeof FundTransactionFeeAllocationSchema>

// commitments linked to a Closing transaction (cfg closes_commitments):
// amount_at_close / prior_* are stamped at Book, null while Draft
export const FundTransactionClosingCommitmentSchema = z
  .object({
    id: z.string(),
    fund_commitment_id: z.string(),
    investors__name: z.string().nullish(),
    accounts__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    amount_at_close: z.number().nullish(),
    prior_status_label: z.string().nullish(),
    prior_date_closed: z.string().nullish()
  })
  .passthrough()

export type FundTransactionClosingCommitment = z.infer<typeof FundTransactionClosingCommitmentSchema>

// one transferee of a Transfer of Interest transaction (cfg
// transfers_interest): amount = the commitment amount it takes; the terms
// are the requested values (null = inherit), effective_* what Post applies
// (requested, else the commitment's current value); prior_* /
// to_fund_commitment_id / transferred_position are stamped at Post (the
// child commitment and what moved to it on a split), null while Draft
export const FundTransactionTransfereeSchema = z
  .object({
    id: z.string(),
    to_investor_id: z.string().nullish(),
    to_investors__name: z.string().nullish(),
    to_accounts__name: z.string().nullish(),
    amount: z.number().nullish(),
    prog__fraction: z.number().nullish(),
    to_fund_commitment_id: z.string().nullish(),
    consideration_amount: z.number().nullish(),
    notes: z.string().nullish(),
    management_fee_rate: z.number().nullish(),
    base_carry_rate: z.number().nullish(),
    base_hurdle_multiple: z.number().nullish(),
    stepped_carry_rate: z.number().nullish(),
    stepped_hurdle_multiple: z.number().nullish(),
    prior_management_fee_rate: z.number().nullish(),
    prior_base_carry_rate: z.number().nullish(),
    prior_base_hurdle_multiple: z.number().nullish(),
    prior_stepped_carry_rate: z.number().nullish(),
    prior_stepped_hurdle_multiple: z.number().nullish(),
    effective_management_fee_rate: z.number().nullish(),
    effective_base_carry_rate: z.number().nullish(),
    effective_base_hurdle_multiple: z.number().nullish(),
    effective_stepped_carry_rate: z.number().nullish(),
    effective_stepped_hurdle_multiple: z.number().nullish(),
    transferred_position: z.record(z.string(), z.unknown()).nullish()
  })
  .passthrough()

export type FundTransactionTransferee = z.infer<typeof FundTransactionTransfereeSchema>

// the transfer of a Transfer of Interest transaction (cfg
// transfers_interest): the commitment being assigned plus one entry per
// transferee; transfer_date / prior_* / position_at_transfer are stamped at
// Post, null while Draft; prog__mode = what Post will do / did: 'whole'
// (one transferee taking the whole commitment - the row is re-pointed) or
// 'split' (a child commitment per transferee, the source reduced). The first
// transferee's keys are repeated at the top level (A3.6 shape).
export const FundTransactionTransferSchema = z
  .object({
    id: z.string(),
    fund_commitment_id: z.string(),
    fund_commitments__amount: z.number().nullish(),
    cfg_fund_commitment_statuses__label: z.string().nullish(),
    investors__name: z.string().nullish(),
    from_investor_id: z.string().nullish(),
    from_investors__name: z.string().nullish(),
    from_accounts__name: z.string().nullish(),
    to_investor_id: z.string().nullish(),
    to_investors__name: z.string().nullish(),
    to_accounts__name: z.string().nullish(),
    transfer_date: z.string().nullish(),
    consideration_amount: z.number().nullish(),
    notes: z.string().nullish(),
    management_fee_rate: z.number().nullish(),
    base_carry_rate: z.number().nullish(),
    base_hurdle_multiple: z.number().nullish(),
    stepped_carry_rate: z.number().nullish(),
    stepped_hurdle_multiple: z.number().nullish(),
    prior_investor_id: z.string().nullish(),
    prior_management_fee_rate: z.number().nullish(),
    prior_base_carry_rate: z.number().nullish(),
    prior_base_hurdle_multiple: z.number().nullish(),
    prior_stepped_carry_rate: z.number().nullish(),
    prior_stepped_hurdle_multiple: z.number().nullish(),
    effective_management_fee_rate: z.number().nullish(),
    effective_base_carry_rate: z.number().nullish(),
    effective_base_hurdle_multiple: z.number().nullish(),
    effective_stepped_carry_rate: z.number().nullish(),
    effective_stepped_hurdle_multiple: z.number().nullish(),
    position_at_transfer: z.record(z.string(), z.unknown()).nullish(),
    prior_amount: z.number().nullish(),
    prior_status_label: z.string().nullish(),
    prog__source_amount: z.number().nullish(),
    prog__total_amount: z.number().nullish(),
    prog__residual_amount: z.number().nullish(),
    prog__transferee_count: z.number().nullish(),
    prog__mode: z.enum(['whole', 'split']).nullish(),
    transferees: z.array(FundTransactionTransfereeSchema).optional().default([])
  })
  .passthrough()

export type FundTransactionTransfer = z.infer<typeof FundTransactionTransferSchema>

// one fee-capital funding source (cfg.fee_funding_sources) as the booking
// form sees it: amount_input = the booking's fund-level amount (null = none
// entered; never for the remainder source), allocated = what this
// transaction's lines take from it, allowed = offered for this fund (a
// deemed distribution is not, on an SBIC)
export const FundTransactionFeeFundingSourceSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    description: z.string().nullish(),
    is_remainder: z.boolean(),
    is_deemed_distribution: z.boolean(),
    allowed: z.boolean(),
    amount_input: z.number().nullish(),
    allocated: z.number()
  })
  .passthrough()

export type FundTransactionFeeFundingSource = z.infer<typeof FundTransactionFeeFundingSourceSchema>

// the Funding section of a fee-paying transaction (spec A3.8); null for a
// type that pays no fees. uncovered_total = the fees paid that the units'
// fee capital does not cover (funded by the sources); reversal_total = pairs
// reversed because fee capital now covers earlier ones; stale = the
// position moved since the last save (Post is refused until re-saved)
export const FundTransactionFeeFundingSchema = z
  .object({
    applies: z.boolean(),
    fee_capital_tracking: z.boolean().nullish(),
    tracked_from: z.string().nullish(),
    uncovered_total: z.number(),
    reversal_total: z.number(),
    funded_total: z.number(),
    stale: z.boolean(),
    sources: z.array(FundTransactionFeeFundingSourceSchema)
  })
  .passthrough()

export type FundTransactionFeeFunding = z.infer<typeof FundTransactionFeeFundingSchema>

export const FundTransactionDetailSchema = z
  .object({
    transaction: FundTransactionHeaderSchema,
    items: z.array(FundTransactionItemSchema),
    journals: z.array(FundTransactionJournalSchema),
    totals: FundTransactionTotalsSchema,
    fee_allocations: z.array(FundTransactionFeeAllocationSchema).optional().default([]),
    fee_funding: FundTransactionFeeFundingSchema.nullish(),
    closing_commitments: z.array(FundTransactionClosingCommitmentSchema).optional().default([]),
    transfer: FundTransactionTransferSchema.nullish(),
    generation: FundTransactionGenerationSchema.nullish()
  })
  .strict()

export type FundTransactionHeader = z.infer<typeof FundTransactionHeaderSchema>
export type FundTransactionItem = z.infer<typeof FundTransactionItemSchema>
export type FundTransactionJournal = z.infer<typeof FundTransactionJournalSchema>
export type FundTransactionTotals = z.infer<typeof FundTransactionTotalsSchema>
export type FundTransactionDetail = z.infer<typeof FundTransactionDetailSchema>

export function validateFundTransactionDetail(data: unknown): FundTransactionDetail {
  try {
    return validateWithZodSchema(FundTransactionDetailSchema, data, 'fund transaction detail')
  } catch (error) {
    throw new Error(`Fund transaction detail validation failed: ${error}`)
  }
}

/** Save payload sent to POST /fund-transactions (ft__save_transaction). */
export interface FundTransactionSavePayload {
  id?: string
  fund_id?: string
  transaction_type_id?: number
  transaction_date?: string
  description?: string | null
  currency_id?: string | null
  capital_call_id?: string | null
  fee_period_start?: string | null
  fee_period_end?: string | null
  items?: Array<{
    id?: string
    acc_item_id: string
    description?: string | null
    amount: number
    currency_id?: string | null
    display_order?: number
  }>
  manual_journals?: Array<{
    id?: string
    acc_item_id: string
    debit_credit_id: 'dr' | 'cr'
    amount: number
    description?: string | null
    fund_commitment_id?: string | null
  }>
  fee_overrides?: Array<{
    fund_commitment_id: string
    override_amount: number | null
  }>
  /** Fee capital tracking (A3.8): fund-level amount per non-remainder funding source (full replacement set). */
  fee_funding?: Array<{
    fee_funding_source_id: string
    amount: number
  }>
  /** Closing types only: full replacement set of the commitments to close on Post. */
  commitment_ids?: string[]
  /** Transfer of Interest types only: the commitment and one entry per transferee (full replacement set). */
  transfer?: {
    fund_commitment_id: string
    transferees: FundTransactionTransfereePayload[]
  }
}

/** One transferee of the save payload: an existing investor OR a new entity, the amount it takes, requested terms (null = inherit), memo price. */
export interface FundTransactionTransfereePayload {
  id?: string
  to_investor_id?: string | null
  new_investor?: {
    account_id: string
    name: string
    legal_name?: string | null
    investor_classification_id?: number | null
    sbic_investor_type_id?: number | null
  } | null
  amount: number
  management_fee_rate?: number | null
  base_carry_rate?: number | null
  base_hurdle_multiple?: number | null
  stepped_carry_rate?: number | null
  stepped_hurdle_multiple?: number | null
  consideration_amount?: number | null
  notes?: string | null
}
